import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { useAlert } from "react-alert";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import Button from "../../waybee-ui/Button";
import BackButton from "../../components/BackButton";
import ClassStagesService, {
  NEW_STAGE_ID
} from "../../services/ClassStagesService";
import StageAssignmentDialog from "./StageAssignmentDialog";

const TITLE_MAX_LENGTH = 255;

const Section = styled.div`
  margin-top: 16px;
  padding: 16px 18px;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  background: #fff;
`;

const SectionTitle = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 17px;
    color: #333;
  }
`;

const SectionHint = styled(Typography)`
  && {
    font-size: 13px;
    color: #777;
    margin-bottom: 8px;
  }
`;

const ConceptLabel = styled(Typography)`
  && {
    display: block;
    font-weight: 600;
    font-size: 14px;
    color: #333;
    margin-bottom: 6px;
  }
`;

const LinkList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const AddLinkChip = styled(Chip)`
  && {
    border-style: dashed;
  }
`;

const EmptyLinks = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  margin-top: 12px;
  padding: 20px 16px;
  border: 1px dashed
    ${({ theme, $error }) => theme.color[$error ? "error" : "primary"].main};
  border-radius: 10px;
  background: transparent;
  color: ${({ theme, $error }) =>
    theme.color[$error ? "error" : "primary"].main};
  cursor: pointer;
  font: inherit;
  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }
`;

const EmptyLinksLabel = styled(Typography)`
  && {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
  }
`;

const Message = styled(Typography)`
  && {
    color: #6b6b6b;
    text-align: center;
    margin: 24px 8px;
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const mergeConcepts = (catalog, stageConcepts) => {
  const descriptions = new Map(
    (stageConcepts || []).map(concept => [concept.code, concept.description])
  );
  const merged = catalog.map(concept => ({
    code: concept.code,
    description: descriptions.get(concept.code) || ""
  }));
  const catalogCodes = new Set(catalog.map(concept => concept.code));
  (stageConcepts || []).forEach(concept => {
    if (!catalogCodes.has(concept.code)) merged.push({ ...concept });
  });
  return merged;
};

const ClassStageForm = ({ match, history, stage, periods, onSaved }) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const classId = match.params.id;

  const conceptLabel = code => {
    const name = t(`classStages.conceptNames.${code}`, { defaultValue: "" });
    return name
      ? t("classStages.form.conceptNamed", { name, code })
      : t("classStages.form.conceptDescription", { code });
  };
  const stageId = decodeURIComponent(match.params.stageId);
  const isNew = stageId === NEW_STAGE_ID;

  const [catalog, setCatalog] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [saving, setSaving] = useState(false);

  const [periodCode, setPeriodCode] = useState(stage ? stage.periodCode : "");
  const [title, setTitle] = useState(stage ? stage.title : "");
  const [description, setDescription] = useState(
    stage ? stage.description : ""
  );
  const [concepts, setConcepts] = useState([]);
  const [links, setLinks] = useState(stage ? stage.links : []);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const missing = {
    period: !periodCode,
    stageTitle: !title.trim(),
    links: !links.length
  };

  const showError = message => {
    alert.show(message, { title: t("common.actions.error") });
  };

  useEffect(() => {
    if (isNew && !periods.length) showError(t("classStages.form.noPeriods"));
  }, []);

  useEffect(() => {
    let active = true;
    ClassStagesService.getConcepts()
      .then(conceptCatalog => {
        if (!active) return;
        setCatalog(conceptCatalog);
        setConcepts(
          mergeConcepts(conceptCatalog.concepts, stage && stage.concepts)
        );
        if (isNew && !conceptCatalog.concepts.length) {
          showError(t("classStages.form.noConcepts"));
        }
      })
      .catch(() => {
        if (!active) return;
        setHasError(true);
        showError(t("classStages.form.conceptsLoadError"));
      });
    return () => {
      active = false;
    };
  }, [stage]);

  const updateConceptDescription = (code, value) => {
    setConcepts(previous =>
      previous.map(concept =>
        concept.code === code ? { ...concept, description: value } : concept
      )
    );
  };

  const listPath = `/classes/${classId}/stages`;

  const periodOptions =
    stage && !periods.some(period => period.code === stage.periodCode)
      ? periods.concat({
          code: stage.periodCode,
          name: stage.periodName || stage.periodCode
        })
      : periods;

  const applyLinks = selectedLinks => {
    setLinks(selectedLinks);
    setAssignmentOpen(false);
  };

  const save = async () => {
    const missingFields = Object.keys(missing).filter(field => missing[field]);
    if (missingFields.length) {
      setShowErrors(true);
      alert.show(
        t("classStages.form.requiredFields", {
          fields: missingFields
            .map(field => t(`classStages.form.${field}`))
            .join(", ")
        }),
        { title: t("common.actions.attention") }
      );
      return;
    }
    setSaving(true);
    try {
      await ClassStagesService.saveStage(classId, {
        id: isNew ? null : stageId,
        periodCode,
        title: title.trim(),
        description,
        concepts,
        links
      });
      onSaved();
    } catch (error) {
      setSaving(false);
      const forbidden = error.response && error.response.status === 403;
      alert.show(
        t(
          forbidden ? "classStages.saveForbidden" : "classStages.form.saveError"
        ),
        { title: t("common.actions.error") }
      );
    }
  };

  const renderForm = () => (
    <>
      <Section>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              required
              variant="outlined"
              label={t("classStages.form.period")}
              value={periodCode}
              onChange={event => setPeriodCode(event.target.value)}
              error={showErrors && missing.period}
              helperText={
                (showErrors &&
                  missing.period &&
                  t("common.actions.required")) ||
                (periodOptions.length ? "" : t("classStages.form.noPeriods"))
              }
            >
              {periodOptions.map(period => (
                <MenuItem key={period.code} value={period.code}>
                  {period.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              required
              variant="outlined"
              label={t("classStages.form.stageTitle")}
              value={title}
              onChange={event => setTitle(event.target.value)}
              inputProps={{ maxLength: TITLE_MAX_LENGTH }}
              error={showErrors && missing.stageTitle}
              helperText={
                showErrors && missing.stageTitle
                  ? t("common.actions.required")
                  : t("classStages.form.stageTitleHelper", {
                      count: title.length,
                      max: TITLE_MAX_LENGTH
                    })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              label={t("classStages.form.description")}
              value={description}
              onChange={event => setDescription(event.target.value)}
            />
          </Grid>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>{t("classStages.form.links")}</SectionTitle>
        {links.length ? (
          <LinkList>
            {links.map(link => (
              <Chip
                key={link.idTurmaDisc}
                variant="outlined"
                label={`${link.className} · ${link.disciplineName}`}
              />
            ))}
            <AddLinkChip
              variant="outlined"
              color="primary"
              icon={<AddIcon />}
              label={t("classStages.assignment.action")}
              onClick={() => setAssignmentOpen(true)}
            />
          </LinkList>
        ) : (
          <EmptyLinks
            type="button"
            $error={showErrors}
            onClick={() => setAssignmentOpen(true)}
          >
            <EmptyLinksLabel>
              <AddIcon />
              {t("classStages.assignment.title")}
            </EmptyLinksLabel>
            <SectionHint component="span">
              {t("classStages.form.linksRequired")}
            </SectionHint>
          </EmptyLinks>
        )}
      </Section>

      <Section>
        <SectionTitle>{t("classStages.form.concepts")}</SectionTitle>
        {catalog && catalog.groupTitle && (
          <SectionHint>
            {t("classStages.form.conceptsGroup", { name: catalog.groupTitle })}
          </SectionHint>
        )}
        {!catalog && (
          <Box textAlign="center" my={2}>
            <CircularProgress size={24} />
          </Box>
        )}
        {catalog && concepts.length > 0 && (
          <Grid container spacing={2}>
            {concepts.map(concept => (
              <Grid item xs={12} key={concept.code}>
                <ConceptLabel
                  component="label"
                  htmlFor={`stage-concept-${concept.code}`}
                >
                  {conceptLabel(concept.code)}
                </ConceptLabel>
                <TextField
                  id={`stage-concept-${concept.code}`}
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  value={concept.description}
                  onChange={event => {
                    updateConceptDescription(concept.code, event.target.value);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
        {catalog && !concepts.length && (
          <SectionHint>{t("classStages.form.noConcepts")}</SectionHint>
        )}
      </Section>

      <FooterActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => history.push(listPath)}
          disabled={saving}
        >
          {t("classStages.form.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {t("classStages.form.save")}
        </Button>
      </FooterActions>
    </>
  );

  const renderContent = () => {
    if (hasError) return <Message>{t("classStages.form.loadError")}</Message>;
    if (!isNew && !stage) {
      return <Message>{t("classStages.form.notFound")}</Message>;
    }
    return renderForm();
  };

  return (
    <Grid container spacing={2}>
      <BackButton to={listPath} />
      <Grid item xs={12}>
        <Group fullWidth>
          <Heading level={2} gutterBottom>
            {isNew
              ? t("classStages.form.newTitle")
              : t("classStages.form.viewTitle")}
          </Heading>
          {renderContent()}
        </Group>
      </Grid>
      <StageAssignmentDialog
        open={assignmentOpen}
        onClose={() => setAssignmentOpen(false)}
        onConfirm={applyLinks}
        stageTitle={title}
        currentClassId={classId}
        initialLinks={links}
      />
    </Grid>
  );
};

ClassStageForm.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      stageId: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  stage: PropTypes.shape({
    periodCode: PropTypes.string,
    periodName: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    concepts: PropTypes.arrayOf(PropTypes.shape({})),
    links: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  periods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSaved: PropTypes.func.isRequired
};

ClassStageForm.defaultProps = {
  stage: null
};

export default withRouter(ClassStageForm);
